import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import {
    ConfigurationService,
    EnvironmentInfo,
    ThemeLoaderService,
} from '@interacta-shared/data-access-configuration';
import { isDefined } from '@interacta-shared/util';
import {
    BOTTOM_MENU_ITEM_IDS,
    BottomMenuService,
} from '@interacta/feature-bottom-menu';
import {
    BulkWorkflowTransitionBannerComponent,
    selectCurrentOperation,
} from '@interacta/feature-bulk-workflow-transition';
import { StateManagerService } from '@modules/state/services/state-manager.service';
import { Store } from '@ngrx/store';
import { firstValueFrom, forkJoin } from 'rxjs';
import { concatMap, filter, tap } from 'rxjs/operators';
import { getMappedLocation } from '../helpers/legacy-path-mappings.utils';

@Injectable({
    providedIn: 'root',
})
export class BootService {
    private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
    private readonly bottomMenuService = inject(BottomMenuService);
    private readonly store = inject(Store);
    private configurationService = inject(ConfigurationService);
    private stateManagerService = inject(StateManagerService);
    private themeLoaderService = inject(ThemeLoaderService);
    private readonly environmentInfo$ = toObservable(
        this.configurationService.environmentInfo,
    );

    private readonly bulkWorkflowTransitionBannerIsOpen = computed(
        () => !!this.store.selectSignal(selectCurrentOperation)(),
    );

    public async initApp(): Promise<void> {
        if (this.legacyPathRewrite()) {
            return;
        }

        this.environmentInfo$
            .pipe(filter(isDefined))
            .subscribe((environmentInfo) =>
                this.initGoogleAnalytics(environmentInfo),
            );

        await firstValueFrom(
            forkJoin([
                this.configurationService.updateTimezones(),
                this.configurationService.updateEnvironmentInfo(),
            ]).pipe(
                tap(() => {
                    this.bottomMenuService.addComponent({
                        id: BOTTOM_MENU_ITEM_IDS.BULK_WORKFLOW_TRANSITION_BANNER,
                        type: BulkWorkflowTransitionBannerComponent,
                        isOpen: this.bulkWorkflowTransitionBannerIsOpen,
                    });
                }),
                concatMap(([_, environmentInfo]) => {
                    const themeUrl =
                        this.configurationService.getThemeConfigUrl();

                    return forkJoin([
                        this.stateManagerService.validateCache(
                            environmentInfo.buildInfo,
                        ),
                        this.themeLoaderService.loadRemoteTheme(themeUrl),
                    ]);
                }),
            ),
        );
    }

    private initGoogleAnalytics(environmentInfo: EnvironmentInfo): void {
        const { googleAnalyticsClientId, plantId, plantBaseUrl } =
            environmentInfo;

        if (googleAnalyticsClientId && isDefined(plantId)) {
            this.googleAnalyticsService.init(
                googleAnalyticsClientId,
                plantId,
                plantBaseUrl,
            );
        }
    }

    private legacyPathRewrite(): boolean {
        const originalLocation = window.location.href;

        const mappedLocation = getMappedLocation(originalLocation);

        if (mappedLocation !== originalLocation) {
            window.location.href = mappedLocation;
            return true;
        }
        return false;
    }
}
